import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faEnvelope, 
  faMobileAlt, 
  faSpinner,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../auth/AuthContext';
import { supabase } from '../../api/supabase';
import { getRelayMessages, sendRelayEmail, sendRelaySMS } from '../../utils/relayService';
import './RelayMessaging.css';

const RelayMessaging = () => {
  const { relayId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [messageType, setMessageType] = useState('email');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch messages on component mount and when relayId changes
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!user || !relayId) {
          setLoading(false);
          return;
        }
        
        const fetchedMessages = await getRelayMessages(relayId, user.id);
        setMessages(fetchedMessages);
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError('Failed to load messages. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
    
    // Set up real-time subscription for new messages
    const setupSubscription = async () => {
      if (!user || !relayId) return;
      
      const subscription = supabase
        .from('relay_messages')
        .on('INSERT', (payload) => {
          if (payload.new.relay_id === relayId) {
            setMessages((prevMessages) => [...prevMessages, payload.new]);
          }
        })
        .subscribe();
      
      return () => {
        supabase.removeSubscription(subscription);
      };
    };
    
    const cleanup = setupSubscription();
    return () => {
      if (cleanup) cleanup();
    };
  }, [relayId, user]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;
    if (messageType === 'email' && !subject.trim()) {
      setError('Please enter a subject for your email');
      return;
    }
    
    try {
      setSending(true);
      setError(null);
      
      if (messageType === 'email') {
        await sendRelayEmail(relayId, user.id, newMessage, subject);
        setSubject('');
      } else {
        await sendRelaySMS(relayId, user.id, newMessage);
      }
      
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="relay-messaging loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="relay-messaging">
      <div className="messaging-header">
        <h2>Secure Communication Channel</h2>
        <div className="message-type-toggle">
          <button 
            className={`toggle-button ${messageType === 'email' ? 'active' : ''}`}
            onClick={() => setMessageType('email')}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            <span>Email</span>
          </button>
          <button 
            className={`toggle-button ${messageType === 'sms' ? 'active' : ''}`}
            onClick={() => setMessageType('sms')}
          >
            <FontAwesomeIcon icon={faMobileAlt} />
            <span>SMS</span>
          </button>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <p>{error}</p>
        </div>
      )}
      
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div 
              key={message.id} 
              className={`message ${message.sender_id === user.id ? 'sent' : 'received'}`}
            >
              <div className="message-content">
                {message.message_type === 'email' && message.subject && (
                  <div className="message-subject">
                    <strong>Subject:</strong> {message.subject}
                  </div>
                )}
                <p>{message.content}</p>
                <div className="message-meta">
                  <span className="message-time">
                    {new Date(message.created_at).toLocaleString()}
                  </span>
                  <span className="message-type">
                    <FontAwesomeIcon 
                      icon={message.message_type === 'email' ? faEnvelope : faMobileAlt} 
                    />
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <form className="message-form" onSubmit={handleSendMessage}>
        {messageType === 'email' && (
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject"
              required={messageType === 'email'}
            />
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="message">Message</label>
          <textarea
            id="message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={`Type your ${messageType} message here...`}
            required
          />
        </div>
        
        <button 
          type="submit" 
          className="send-button"
          disabled={sending}
        >
          {sending ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <>
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>Send {messageType === 'email' ? 'Email' : 'SMS'}</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default RelayMessaging; 